import axios from 'axios';
import { toFriendlyErrorMessage } from '../utils/friendlyError';

export type ApiError = {
  message: string;
  code?: string;
  statusCode?: number;
  requestId?: string | null;
};

type ErrorBody = { message?: string; code?: string; requestId?: string };

function isNormalizedApiError(error: unknown): error is ApiError {
  if (!error || typeof error !== 'object' || axios.isAxiosError(error)) {
    return false;
  }
  const candidate = error as ApiError;
  return (
    typeof candidate.message === 'string' &&
    (typeof candidate.code === 'string' ||
      typeof candidate.statusCode === 'number' ||
      candidate.requestId !== undefined)
  );
}

/** If the DB/API sent `CODE: human text`, keep the human text. */
export function displayApiMessage(message: string): string {
  const trimmed = message.trim();
  const match = trimmed.match(/^([A-Z][A-Z0-9_]{3,80}):\s*(.+)$/);
  return match ? match[2].trim() : trimmed;
}

export function normalizeApiError(error: unknown): ApiError {
  if (isNormalizedApiError(error)) {
    return {
      ...error,
      message: toFriendlyErrorMessage({
        ...error,
        message: displayApiMessage(error.message),
      }),
    };
  }

  if (!axios.isAxiosError<ErrorBody>(error)) {
    return { message: 'Something went wrong. Please try again.' };
  }

  if (error.code === 'ECONNABORTED') {
    return { message: 'The request timed out.', code: 'REQUEST_TIMEOUT' };
  }

  if (!error.response) {
    return {
      message: 'Unable to connect to the server. Check your connection and try again.',
      code: 'NETWORK_ERROR',
    };
  }

  const data = error.response.data;
  const headerRequestId = error.response.headers?.['x-request-id'];
  const statusCode = error.response.status;
  const rawMessage =
    typeof data?.message === 'string' ? data.message.trim() : '';
  const displayed = rawMessage ? displayApiMessage(rawMessage) : '';
  const prefixMatch = rawMessage.match(/^([A-Z][A-Z0-9_]{3,80}):\s*(.+)$/);
  const code =
    (typeof data?.code === 'string' && data.code.trim()) ||
    prefixMatch?.[1] ||
    undefined;

  const normalized: ApiError = {
    message: displayed || 'Something went wrong. Please try again.',
    code,
    statusCode,
    requestId: data?.requestId || headerRequestId || null,
  };

  return {
    ...normalized,
    message: toFriendlyErrorMessage(normalized),
  };
}
